import { computed, ref } from "vue";

type AppRole = "admin" | "coach" | "assistant" | "supervision_coordinator";

interface AuthUser {
  username: string;
  roles: AppRole[];
  coachUserId: string | null;
  phone?: string | null;
  displayName?: string | null;
  accountCode?: string | null;
}

const TOKEN_KEY = "token";

const token = ref<string | null>(localStorage.getItem(TOKEN_KEY));
const user = ref<AuthUser | null>(null);
const roleDisplayNames = ref<Record<string, string> | null>(null);

const isLoggedIn = computed(() => token.value !== null);
const isAdmin = computed(() => user.value?.roles?.includes("admin") ?? false);
const isCoach = computed(() => user.value?.roles?.includes("coach") ?? false);
const isAssistant = computed(() => user.value?.roles?.includes("assistant") ?? false);
const isSupervisionCoordinator = computed(() => user.value?.roles?.includes("supervision_coordinator") ?? false);
const multiRoleName = computed(() => {
  if (!roleDisplayNames.value) return user.value?.displayName || user.value?.phone || user.value?.username || "";
  const unique = [...new Set(Object.values(roleDisplayNames.value))];
  return unique.length <= 1 ? unique[0]! : unique.join(" / ");
});

const roleLabel = computed(() => {
  const m: Record<string, string> = { admin: "管理员", coach: "教练", assistant: "小棠X", supervision_coordinator: "督导协调运营" };
  return (user.value?.roles ?? []).map((role) => m[role]).filter(Boolean).join(" · ");
});

function setToken(value: string): void {
  token.value = value;
  localStorage.setItem(TOKEN_KEY, value);
}

function clearToken(): void {
  token.value = null;
  user.value = null;
  localStorage.removeItem(TOKEN_KEY);
}

function normalizeAuthUser(value: unknown): AuthUser | null {
  if (typeof value !== "object" || value === null) return null;
  const raw = value as Record<string, unknown>;
  const validRoles: AppRole[] = ["admin", "coach", "assistant", "supervision_coordinator"];
  const roles = Array.isArray(raw.roles)
    ? raw.roles.filter((role): role is AppRole => typeof role === "string" && validRoles.includes(role as AppRole))
    : typeof raw.role === "string" && validRoles.includes(raw.role as AppRole) ? [raw.role as AppRole] : [];
  if (typeof raw.username !== "string" || roles.length === 0) return null;
  roleDisplayNames.value = (typeof raw.roleDisplayNames === "object" && raw.roleDisplayNames !== null)
    ? raw.roleDisplayNames as Record<string, string>
    : null;
  return {
    username: raw.username,
    roles,
    coachUserId: typeof raw.coachUserId === "string" ? raw.coachUserId : null,
    phone: typeof raw.phone === "string" ? raw.phone : null,
    displayName: typeof raw.displayName === "string" ? raw.displayName : null,
    accountCode: typeof raw.accountCode === "string" ? raw.accountCode : null,
  };
}

async function login(account: string, password: string): Promise<void> {
  const body: Record<string, string> = { account, password };
  const response = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const payload: unknown = await readResponsePayload(response);
  if (!response.ok) {
    const message =
      typeof payload === "object" && payload !== null
        ? (payload as Record<string, unknown>).message ?? "登录失败"
        : "登录失败";
    throw new Error(String(message));
  }
  if (typeof payload !== "object" || payload === null) throw new Error("登录响应格式不正确");
  const data = payload as { token?: unknown; user?: unknown };
  const authenticatedUser = normalizeAuthUser(data.user);
  if (typeof data.token !== "string" || !authenticatedUser) throw new Error("登录响应格式不正确");
  setToken(data.token);
  user.value = authenticatedUser;
}

async function readResponsePayload(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

async function fetchMe(): Promise<void> {
  if (!token.value) return;
  try {
    const response = await fetch("/api/auth/me", {
      headers: { Authorization: `Bearer ${token.value}` },
    });
    if (!response.ok) { clearToken(); return; }
    const authenticatedUser = normalizeAuthUser(await readResponsePayload(response));
    if (!authenticatedUser) { clearToken(); return; }
    user.value = authenticatedUser;
  } catch { clearToken(); }
}

function logout(): void {
  clearToken();
}

export function useAuth() {
  return { token, user, isLoggedIn, isAdmin, isCoach, isAssistant, isSupervisionCoordinator, multiRoleName, roleLabel, login, fetchMe, logout };
}

export function getToken(): string | null {
  return token.value;
}
